<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">State Dashboard</p>
        <h1 class="page-title">{{ summary?.state || 'State Benchmark' }}</h1>
        <p class="page-subtitle">{{ summary?.state_health_org_name || 'State Health Organization' }}</p>
      </div>
    </div>

    <section class="dashboard-filter-panel dashboard-filter-panel--state">
      <div class="form-section">
        <label class="field-label" for="state-start-date">Start date</label>
        <input id="state-start-date" type="date" class="ui-input" v-model="filters.start_date" @change="loadAll">
      </div>
      <div class="form-section">
        <label class="field-label" for="state-end-date">End date</label>
        <input id="state-end-date" type="date" class="ui-input" v-model="filters.end_date" @change="loadAll">
      </div>
      <div class="form-section">
        <label class="field-label" for="state-clinic-scope">Clinic scope</label>
        <select id="state-clinic-scope" class="ui-input" v-model="clinicScope" @change="handleScopeChange">
          <option value="all">All assigned clinics</option>
          <option value="selected">Selected clinics</option>
        </select>
      </div>
    </section>

    <section v-if="clinicScope === 'selected'" class="selector-card">
      <div class="section-heading">
        <div>
          <h2 class="section-title">Clinic selection</h2>
          <p class="section-description">Search and tick clinics to refine dashboard metrics and charts.</p>
        </div>
        <div class="selector-actions">
          <button type="button" class="ghost-chip" @click="selectAllVisibleClinics">Select all</button>
          <button type="button" class="ghost-chip" @click="clearSelectedClinics">Clear</button>
          <span class="badge-soft">{{ selectedClinicIds.length }} selected</span>
        </div>
      </div>

      <div v-if="selectedClinicIds.length" class="selected-chip-row">
        <button
          v-for="clinic in selectedClinicRows"
          :key="`chip-${clinic.location_uuid}`"
          type="button"
          class="clinic-chip"
          @click="toggleClinic(clinic.location_uuid, false)"
          :title="`Remove ${clinic.clinic_name || 'clinic'}`"
        >
          <span>{{ clinic.clinic_name || 'Unnamed clinic' }}</span>
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="form-section clinic-search">
        <label class="field-label" for="clinic-search">Search clinics</label>
        <input
          id="clinic-search"
          type="search"
          class="ui-input"
          v-model="clinicSearch"
          placeholder="Search by clinic, organization, or city"
        >
      </div>

      <div class="clinic-option-grid">
        <label v-for="clinic in filteredClinics" :key="clinic.location_uuid" class="clinic-option">
          <input
            type="checkbox"
            :checked="selectedClinicIds.includes(clinic.location_uuid)"
            @change="toggleClinic(clinic.location_uuid, $event.target.checked)"
          >
          <span>
            <strong>{{ clinic.clinic_name || 'Unnamed clinic' }}</strong>
            <small>{{ clinic.organization_name || clinic.city || clinic.state || 'Watchable clinic' }}</small>
          </span>
        </label>
        <div v-if="filteredClinics.length === 0" class="empty-selector">
          No clinics match your search.
        </div>
      </div>
    </section>

    <div v-if="pageError" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ pageError }}
    </div>

    <div class="metrics-grid">
      <article v-for="metric in summaryCards" :key="metric.key" class="metric-card" :class="`metric-card--${metric.variant}`">
        <div class="metric-card__icon">
          <i :class="metric.icon"></i>
        </div>
        <div class="metric-card__content">
          <span class="metric-card__label">{{ metric.label }}</span>
          <strong class="metric-card__value">{{ metric.value }}</strong>
          <span class="metric-card__hint">{{ metric.hint }}</span>
        </div>
      </article>
    </div>

    <section class="state-chart-grid">
      <article class="chart-card chart-card--span-8">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Screening activity</h2>
            <p class="section-description">Daily screening, patient, abnormal, and Grade 2 movement for {{ clinicScopeLabel }}.</p>
          </div>
        </div>
        <div class="chart-wrap">
          <Line v-if="hasDailyActivity" :data="dailyActivityChartData" :options="lineChartOptions" />
          <div v-else class="empty-chart">No trend data in this period.</div>
        </div>
      </article>

      <article class="chart-card chart-card--span-4">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Grade mix</h2>
            <p class="section-description">True DR grade distribution for {{ clinicScopeLabel }}.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Pie v-if="hasGradeDistribution" :data="gradeDistributionChartData" :options="pieChartOptions" />
          <div v-else class="empty-chart">No grade data.</div>
        </div>
      </article>

      <article class="chart-card chart-card--span-4">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Abnormal findings</h2>
            <p class="section-description">Abnormal vs normal case split for {{ clinicScopeLabel }}.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Pie v-if="hasAbnormalDistribution" :data="abnormalDistributionChartData" :options="pieChartOptions" />
          <div v-else class="empty-chart">No abnormality data.</div>
        </div>
      </article>

      <article class="chart-card chart-card--span-4">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Clinic volume ranking</h2>
            <p class="section-description">Top active clinics by screenings for {{ clinicScopeLabel }}.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Bar v-if="hasClinicVolume" :data="clinicVolumeChartData" :options="horizontalBarOptions" />
          <div v-else class="empty-chart">No clinic volume yet.</div>
        </div>
      </article>

      <article class="chart-card chart-card--span-4">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Growth leaderboard</h2>
            <p class="section-description">Period-over-period screening growth for {{ clinicScopeLabel }}.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Bar v-if="hasGrowthData" :data="growthChartData" :options="growthBarOptions" />
          <div v-else class="empty-chart">No growth signal yet.</div>
        </div>
      </article>

      <article class="chart-card chart-card--wide">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Image quality by clinic</h2>
            <p class="section-description">Useful for finding facilities that need capture training or device review.</p>
          </div>
        </div>
        <div class="chart-wrap">
          <Bar v-if="hasQualityData" :data="qualityChartData" :options="qualityBarChartOptions" />
          <div v-else class="empty-chart">No quality data in this period.</div>
        </div>
      </article>

      <article class="chart-card chart-card--wide">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Image quality by camera</h2>
            <p class="section-description">Weighted by screening volume, so high-volume clinics influence the model score fairly.</p>
          </div>
        </div>
        <div class="chart-wrap">
          <Bar v-if="hasCameraQualityData" :data="cameraQualityChartData" :options="qualityBarChartOptions" />
          <div v-else class="empty-chart">No camera quality data in this period.</div>
        </div>
        <div v-if="hasCameraQualityData" class="camera-quality-table">
          <div class="camera-quality-row camera-quality-row--header">
            <span>Camera</span>
            <span>Avg quality %</span>
            <span>Screenings</span>
            <span>Clinics</span>
          </div>
          <div v-for="row in cameraQualityRows" :key="row.camera_name" class="camera-quality-row">
            <span>{{ row.camera_name }}</span>
            <strong>{{ formatQualityPercent(row.avg_image_quality) }}</strong>
            <span>{{ formatNumber(row.screenings) }}</span>
            <span>{{ formatNumber(row.clinic_count) }}</span>
          </div>
        </div>
      </article>
    </section>

    <div class="table-card" id="clinic-comparison">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Clinic</th>
            <th class="text-start">Camera</th>
            <th class="text-start">Screenings</th>
            <th class="text-start">Unique patients</th>
            <th class="text-start">Abnormal rate</th>
            <th class="text-start">Grade 2 rate</th>
            <th class="text-start">Avg quality %</th>
            <th class="text-start">Growth</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in scopedClinics" :key="row.location_uuid" class="clickable-row" @click="openClinicDetail(row)">
            <td>
              <span class="table-primary">{{ row.clinic_name || 'Unnamed clinic' }}</span>
              <span class="table-secondary">{{ row.organization_name || row.city || row.state || 'Watchable clinic' }}</span>
            </td>
            <td>
              <span class="table-primary">{{ row.fundus_camera_name || 'Not assigned' }}</span>
              <span class="table-secondary">{{ row.fundus_camera_brand || 'Camera' }}</span>
            </td>
            <td>
              <span class="table-primary">{{ row.metrics?.screening_volume ?? 0 }}</span>
              <span class="table-secondary">{{ row.metrics?.active_days ?? 0 }} active days</span>
            </td>
            <td>
              <span class="table-primary">{{ row.metrics?.unique_patients_screened ?? 0 }}</span>
              <span class="table-secondary">Patients screened</span>
            </td>
            <td>
              <span class="table-primary">{{ formatRate(row.metrics?.abnormal_rate) }}</span>
              <span class="table-secondary">{{ row.metrics?.abnormal_cases ?? 0 }} cases</span>
            </td>
            <td>
              <span class="table-primary">{{ formatRate(row.metrics?.grade_2_rate) }}</span>
              <span class="table-secondary">{{ row.metrics?.grade_2_cases ?? 0 }} cases</span>
            </td>
            <td>
              <span class="table-primary">{{ formatQualityPercent(row.metrics?.avg_image_quality) }}</span>
              <span class="table-secondary">Image quality</span>
            </td>
            <td>
              <span class="table-primary">{{ formatGrowth(row.metrics?.growth_rate) }}</span>
              <span class="table-secondary">Prev {{ row.metrics?.previous_screening_volume ?? 0 }}</span>
            </td>
          </tr>
          <tr v-if="scopedClinics.length === 0">
            <td colspan="8">
              <div class="empty-state">
                <strong>No clinics to show.</strong>
                <p v-if="clinicScope === 'selected'">Select one or more clinics above, or switch to all assigned clinics.</p>
                <p v-else>Ask admin to assign watchable clinics for this state organization.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selectedClinicDetail || isDetailLoading" class="detail-panel">
      <div class="section-heading">
        <div>
          <h2 class="section-title">{{ selectedClinicDetail?.clinic_name || 'Loading clinic detail' }}</h2>
          <p class="section-description">
            {{ selectedClinicDetail?.state || summary?.state }} clinic performance for the selected period.
          </p>
        </div>
        <button type="button" class="icon-button-soft" @click="selectedClinicDetail = null" title="Close detail">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div v-if="isDetailLoading" class="empty-state">Loading clinic detail...</div>
      <div v-else class="detail-grid">
        <div class="stat-card">
          <span class="stat-label">Screenings</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.screening_volume ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Unique patients</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.unique_patients_screened ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Active days</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.active_days ?? 0 }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">Abnormal cases</span>
          <strong class="stat-value">{{ selectedClinicDetail.metrics?.abnormal_cases ?? 0 }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading.vue';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
} from 'chart.js';
import { Pie, Line, Bar } from 'vue-chartjs';
import { getStateOrgClinicDetail, getStateOrgOverview } from '@/services/comparisonServices';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement
);

function toISODate(inputDate) {
  const date = inputDate instanceof Date ? inputDate : new Date(inputDate);
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'StateDashboard',
  components: {
    Loading,
    Pie,
    Line,
    Bar,
  },
  data() {
    const today = new Date();
    const endDate = toISODate(today);
    const startDate = toISODate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000));

    return {
      filters: {
        start_date: startDate,
        end_date: endDate,
      },
      summary: null,
      clinics: [],
      clinicScope: 'all',
      selectedClinicIds: [],
      clinicSearch: '',
      trends: null,
      selectedClinicDetail: null,
      isLoading: false,
      isDetailLoading: false,
      pageError: '',
      scopeReloadTimer: null,
      pieChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
        },
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { position: 'bottom' },
        },
        scales: {
          y: { beginAtZero: true },
        },
      },
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: { beginAtZero: true },
        },
      },
      qualityBarChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      },
      horizontalBarOptions: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: { beginAtZero: true },
        },
      },
      growthBarOptions: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    };
  },
  computed: {
    scopedClinics() {
      if (this.clinicScope !== 'selected') {
        return this.clinics;
      }

      const selected = new Set(this.selectedClinicIds);
      return this.clinics.filter((row) => selected.has(row.location_uuid));
    },
    selectedClinicRows() {
      const selected = new Set(this.selectedClinicIds);
      return this.clinics.filter((row) => selected.has(row.location_uuid));
    },
    filteredClinics() {
      const query = this.clinicSearch.trim().toLowerCase();
      if (!query) {
        return this.clinics;
      }

      return this.clinics.filter((clinic) => {
        const haystack = [
          clinic.clinic_name,
          clinic.organization_name,
          clinic.fundus_camera_name,
          clinic.fundus_camera_brand,
          clinic.fundus_camera_model,
          clinic.city,
          clinic.state,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    },
    clinicScopeLabel() {
      if (this.clinicScope !== 'selected') {
        return 'all assigned clinics';
      }
      const count = this.scopedClinics.length;
      return count === 1 ? '1 selected clinic' : `${count} selected clinics`;
    },
    dateRangeLabel() {
      if (!this.filters.start_date || !this.filters.end_date) {
        return 'selected period';
      }
      return `${this.formatDateLabel(this.filters.start_date)} - ${this.formatDateLabel(this.filters.end_date)}`;
    },
    scopeInsightTitle() {
      if (this.clinicScope === 'selected') {
        return this.selectedClinicIds.length
          ? `${this.selectedClinicIds.length} selected clinics`
          : 'No clinics selected';
      }
      return this.summary?.state || 'All assigned clinics';
    },
    scopeInsightStatement() {
      const screenings = this.formatNumber(this.scopedTotals.screening_volume);
      const patients = this.formatNumber(this.scopedTotals.unique_patients_screened);
      const abnormal = this.formatRate(this.scopedTotals.abnormal_rate);
      const quality = this.formatQualityPercent(this.scopedTotals.avg_image_quality);

      if (this.clinicScope === 'selected' && !this.selectedClinicIds.length) {
        return 'Select clinics below to filter metrics, charts, and daily activity for this period.';
      }

      return `${screenings} screenings and ${patients} unique patients for ${this.clinicScopeLabel} during ${this.dateRangeLabel}, with ${abnormal} abnormal rate and ${quality} average image quality.`;
    },
    scopedTotals() {
      const totals = this.scopedClinics.reduce((accumulator, row) => {
        const metrics = row.metrics || {};
        const quality = metrics.avg_image_quality;
        accumulator.screening_volume += metrics.screening_volume || 0;
        accumulator.unique_patients_screened += metrics.unique_patients_screened || 0;
        accumulator.abnormal_cases += metrics.abnormal_cases || 0;
        accumulator.grade_1_cases += metrics.grade_1_cases || 0;
        accumulator.grade_2_cases += metrics.grade_2_cases || 0;
        if (quality !== null && quality !== undefined) {
          accumulator.qualityTotal += Number(quality);
          accumulator.qualityCount += 1;
        }
        return accumulator;
      }, {
        screening_volume: 0,
        unique_patients_screened: 0,
        abnormal_cases: 0,
        grade_1_cases: 0,
        grade_2_cases: 0,
        qualityTotal: 0,
        qualityCount: 0,
      });

      return {
        ...totals,
        abnormal_rate: totals.screening_volume > 0 ? totals.abnormal_cases / totals.screening_volume : 0,
        grade_2_rate: totals.screening_volume > 0 ? totals.grade_2_cases / totals.screening_volume : 0,
        avg_image_quality: totals.qualityCount > 0 ? totals.qualityTotal / totals.qualityCount : null,
      };
    },
    activeClinicCount() {
      return this.scopedClinics.filter((row) => (row.metrics?.screening_volume || 0) > 0).length;
    },
    qualityRiskCount() {
      return this.scopedClinics.filter((row) => row.metrics?.avg_image_quality !== null && row.metrics?.avg_image_quality < 0.65).length;
    },
    stateGrade2Rate() {
      return this.scopedTotals.grade_2_rate;
    },
    abnormalCaseCount() {
      return this.scopedTotals.abnormal_cases;
    },
    grade2CaseCount() {
      return this.scopedTotals.grade_2_cases;
    },
    lowQualityRate() {
      const qualityRows = this.scopedClinics.filter((row) => row.metrics?.avg_image_quality !== null && row.metrics?.avg_image_quality !== undefined);
      if (!qualityRows.length) return 0;
      return this.qualityRiskCount / qualityRows.length;
    },
    summaryCards() {
      const medianVolume = this.summary?.benchmarks?.median_screening_volume;
      const medianAbnormal = this.summary?.benchmarks?.median_abnormal_rate;
      const showBenchmarks = this.clinicScope === 'all' && this.summary?.benchmarks;

      return [
        {
          key: 'screenings',
          label: 'Screenings',
          value: this.formatNumber(this.scopedTotals.screening_volume),
          hint: showBenchmarks
            ? `Median clinic volume ${this.formatNumber(medianVolume)}`
            : `Total images/cases for ${this.clinicScopeLabel}`,
          icon: 'fas fa-image',
          variant: 'primary',
        },
        {
          key: 'patients',
          label: 'Unique patients',
          value: this.formatNumber(this.scopedTotals.unique_patients_screened),
          hint: `Distinct patients screened for ${this.clinicScopeLabel}`,
          icon: 'fas fa-user-plus',
          variant: 'success',
        },
        {
          key: 'active-clinics',
          label: 'Active clinics',
          value: this.formatNumber(this.activeClinicCount),
          hint: `${this.formatNumber(this.scopedClinics.length)} clinics in current scope`,
          icon: 'fas fa-hospital',
          variant: 'primary',
        },
        {
          key: 'abnormal',
          label: 'Abnormal cases',
          value: this.formatNumber(this.abnormalCaseCount),
          hint: showBenchmarks
            ? `${this.formatRate(this.scopedTotals.abnormal_rate)} vs median ${this.formatRate(medianAbnormal)}`
            : `${this.formatRate(this.scopedTotals.abnormal_rate)} abnormal rate`,
          icon: 'fas fa-triangle-exclamation',
          variant: 'warning',
        },
        {
          key: 'grade2',
          label: 'Grade 2 cases',
          value: this.formatNumber(this.grade2CaseCount),
          hint: `${this.formatRate(this.stateGrade2Rate)} Grade 2 rate`,
          icon: 'fas fa-circle-exclamation',
          variant: 'danger',
        },
        {
          key: 'quality',
          label: 'Average quality',
          value: this.formatQualityPercent(this.scopedTotals.avg_image_quality),
          hint: `Average capture quality for ${this.clinicScopeLabel}`,
          icon: 'fas fa-camera-retro',
          variant: (this.scopedTotals.avg_image_quality || 0) < 0.65 ? 'warning' : 'primary',
        },
        {
          key: 'quality-risk',
          label: 'Quality risk',
          value: this.formatRate(this.lowQualityRate),
          hint: `${this.qualityRiskCount} clinics below 0.65 average quality`,
          icon: 'fas fa-camera',
          variant: this.qualityRiskCount ? 'danger' : 'success',
        },
      ];
    },
    hasDailyActivity() {
      return (this.trends?.daily_activity || []).some((entry) => (entry.screenings || 0) > 0);
    },
    hasGradeDistribution() {
      const distribution = this.trends?.grade_distribution || {};
      return Object.values(distribution).some((value) => Number(value || 0) > 0);
    },
    hasAbnormalDistribution() {
      const distribution = this.trends?.abnormal_distribution || {};
      return Object.values(distribution).some((value) => Number(value || 0) > 0);
    },
    hasClinicVolume() {
      return this.scopedClinics.some((row) => (row.metrics?.screening_volume || 0) > 0);
    },
    hasGrowthData() {
      return this.growthRows.length > 0;
    },
    hasQualityData() {
      return this.qualityRows.some((row) => row.avg_image_quality !== null && row.avg_image_quality !== undefined);
    },
    hasCameraQualityData() {
      return this.cameraQualityRows.some((row) => row.avg_image_quality !== null && row.avg_image_quality !== undefined);
    },
    dailyActivityChartData() {
      const rows = this.trends?.daily_activity || [];
      return {
        labels: rows.map((row) => row.date),
        datasets: [
          {
            label: 'Screenings',
            data: rows.map((row) => row.screenings || 0),
            borderColor: '#0fbab2',
            backgroundColor: 'rgba(15, 186, 178, 0.12)',
            tension: 0.3,
            fill: true,
          },
          {
            label: 'Unique patients',
            data: rows.map((row) => row.unique_patients || 0),
            borderColor: '#6e2b92',
            backgroundColor: 'rgba(110, 43, 146, 0.08)',
            tension: 0.3,
          },
          {
            label: 'Abnormal cases',
            data: rows.map((row) => row.abnormal_cases || 0),
            borderColor: '#dc362e',
            backgroundColor: 'rgba(220, 54, 46, 0.08)',
            tension: 0.3,
          },
          {
            label: 'Grade 2',
            data: rows.map((row) => row.grade_2_cases || 0),
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.08)',
            tension: 0.3,
          },
        ],
      };
    },
    gradeDistributionChartData() {
      const distribution = this.trends?.grade_distribution || {};
      return {
        labels: ['Grade 2', 'Grade 1', 'Grade 0', 'Normal'],
        datasets: [
          {
            data: [
              distribution.Grade_2 || 0,
              distribution.Grade_1 || 0,
              distribution.Grade_0 || 0,
              distribution.Normal || 0,
            ],
            backgroundColor: ['#dc362e', '#f59e0b', '#64748b', '#0fbab2'],
          },
        ],
      };
    },
    abnormalDistributionChartData() {
      const distribution = this.trends?.abnormal_distribution || {};
      return {
        labels: ['Abnormal', 'Normal', 'Unknown'],
        datasets: [
          {
            data: [
              distribution.abnormal || 0,
              distribution.normal || 0,
              distribution.unknown || 0,
            ],
            backgroundColor: ['#dc362e', '#0fbab2', '#94a3b8'],
          },
        ],
      };
    },
    topClinicRows() {
      return [...this.scopedClinics]
        .sort((left, right) => (right.metrics?.screening_volume || 0) - (left.metrics?.screening_volume || 0))
        .slice(0, 10);
    },
    clinicVolumeChartData() {
      return {
        labels: this.topClinicRows.map((row) => row.clinic_name || 'Unnamed clinic'),
        datasets: [
          {
            data: this.topClinicRows.map((row) => row.metrics?.screening_volume || 0),
            backgroundColor: '#0fbab2',
            borderRadius: 8,
          },
        ],
      };
    },
    growthRows() {
      return [...this.scopedClinics]
        .filter((row) => row.metrics?.growth_rate !== null && row.metrics?.growth_rate !== undefined)
        .sort((left, right) => (right.metrics?.growth_rate || 0) - (left.metrics?.growth_rate || 0))
        .slice(0, 10);
    },
    growthChartData() {
      return {
        labels: this.growthRows.map((row) => row.clinic_name || 'Unnamed clinic'),
        datasets: [
          {
            data: this.growthRows.map((row) => Number(((row.metrics?.growth_rate || 0) * 100).toFixed(1))),
            backgroundColor: this.growthRows.map((row) => (row.metrics?.growth_rate || 0) >= 0 ? '#23bd73' : '#dc362e'),
            borderRadius: 8,
          },
        ],
      };
    },
    qualityRows() {
      const fromTrends = (this.trends?.quality_by_clinic || [])
        .filter((row) => row.avg_image_quality !== null && row.avg_image_quality !== undefined)
        .map((row) => ({
          clinic_name: row.clinic_name,
          avg_image_quality: row.avg_image_quality,
        }));

      if (fromTrends.length) {
        return [...fromTrends]
          .sort((left, right) => (left.avg_image_quality || 0) - (right.avg_image_quality || 0))
          .slice(0, 12);
      }

      return [...this.scopedClinics]
        .filter((row) => row.metrics?.avg_image_quality !== null && row.metrics?.avg_image_quality !== undefined)
        .map((row) => ({
          clinic_name: row.clinic_name,
          avg_image_quality: row.metrics.avg_image_quality,
        }))
        .sort((left, right) => (left.avg_image_quality || 0) - (right.avg_image_quality || 0))
        .slice(0, 12);
    },
    qualityChartData() {
      return {
        labels: this.qualityRows.map((row) => row.clinic_name || 'Unknown clinic'),
        datasets: [
          {
            data: this.qualityRows.map((row) => Number(((row.avg_image_quality || 0) * 100).toFixed(1))),
            backgroundColor: this.qualityRows.map((row) => (row.avg_image_quality || 0) < 0.65 ? '#dc362e' : '#0fbab2'),
            borderRadius: 8,
          },
        ],
      };
    },
    cameraQualityRows() {
      const groups = this.scopedClinics.reduce((accumulator, row) => {
        const quality = row.metrics?.avg_image_quality;
        const screenings = Number(row.metrics?.screening_volume || 0);
        const cameraName = row.fundus_camera_name || 'Camera not assigned';

        if (quality === null || quality === undefined) {
          return accumulator;
        }

        if (!accumulator[cameraName]) {
          accumulator[cameraName] = {
            camera_name: cameraName,
            screenings: 0,
            clinic_count: 0,
            weighted_quality_total: 0,
            avg_image_quality: null,
          };
        }

        const weight = screenings > 0 ? screenings : 1;
        accumulator[cameraName].screenings += screenings;
        accumulator[cameraName].clinic_count += 1;
        accumulator[cameraName].weighted_quality_total += Number(quality) * weight;
        accumulator[cameraName].weight_total = (accumulator[cameraName].weight_total || 0) + weight;

        return accumulator;
      }, {});

      return Object.values(groups)
        .map((row) => ({
          ...row,
          avg_image_quality: row.weight_total > 0 ? row.weighted_quality_total / row.weight_total : null,
        }))
        .sort((left, right) => (right.avg_image_quality || 0) - (left.avg_image_quality || 0));
    },
    cameraQualityChartData() {
      return {
        labels: this.cameraQualityRows.map((row) => row.camera_name),
        datasets: [
          {
            data: this.cameraQualityRows.map((row) => Number(((row.avg_image_quality || 0) * 100).toFixed(1))),
            backgroundColor: this.cameraQualityRows.map((row) => (row.avg_image_quality || 0) < 0.65 ? '#dc362e' : '#0fbab2'),
            borderRadius: 8,
          },
        ],
      };
    },
  },
  watch: {
    selectedClinicIds() {
      if (this.clinicScope !== 'selected') {
        return;
      }
      this.queueScopedReload();
    },
  },
  created() {
    this.loadAll();
  },
  beforeUnmount() {
    if (this.scopeReloadTimer) {
      clearTimeout(this.scopeReloadTimer);
    }
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatRate(value) {
      if (value === null || value === undefined) return '0%';
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatGrowth(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatNullableNumber(value) {
      if (value === null || value === undefined) return '-';
      return Number(value).toFixed(2);
    },
    formatQualityPercent(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(0)}%`;
    },
    formatDateLabel(value) {
      if (!value) return '--';
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    handleScopeChange() {
      if (this.clinicScope === 'all') {
        this.loadAll();
        return;
      }
      this.loadAll();
    },
    toggleClinic(locationUuid, checked) {
      if (checked) {
        this.selectedClinicIds = [...new Set([...this.selectedClinicIds, locationUuid])];
        return;
      }
      this.selectedClinicIds = this.selectedClinicIds.filter((uuid) => uuid !== locationUuid);
    },
    selectAllVisibleClinics() {
      const visibleIds = this.filteredClinics.map((clinic) => clinic.location_uuid);
      this.selectedClinicIds = [...new Set([...this.selectedClinicIds, ...visibleIds])];
    },
    clearSelectedClinics() {
      this.selectedClinicIds = [];
    },
    queueScopedReload() {
      if (this.scopeReloadTimer) {
        clearTimeout(this.scopeReloadTimer);
      }
      this.scopeReloadTimer = setTimeout(() => {
        this.loadAll({ silent: true });
      }, 350);
    },
    async openClinicDetail(row) {
      this.isDetailLoading = true;
      this.selectedClinicDetail = {
        clinic_name: row.clinic_name,
        state: row.state,
        metrics: row.metrics || {},
      };
      try {
        const result = await getStateOrgClinicDetail({
          location_uuid: row.location_uuid,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        });
        this.selectedClinicDetail = result.data || this.selectedClinicDetail;
      } catch (error) {
        this.pageError = error.response?.data?.detail || 'Unable to load clinic detail.';
      } finally {
        this.isDetailLoading = false;
      }
    },
    async loadAll({ silent = false } = {}) {
      this.pageError = '';
      if (!silent) {
        this.isLoading = true;
      }
      try {
        const locationUuids = this.clinicScope === 'selected'
          ? this.selectedClinicIds
          : undefined;

        const result = await getStateOrgOverview({
          ...this.filters,
          location_uuids: locationUuids,
          sort: 'screening_volume',
          order: 'desc',
        });
        const payload = result.data || {};
        this.summary = payload.summary || null;
        this.clinics = payload.clinics || [];
        this.trends = payload.trends || null;

        const availableIds = new Set(this.clinics.map((clinic) => clinic.location_uuid));
        const nextSelected = this.selectedClinicIds.filter((clinicId) => availableIds.has(clinicId));
        if (nextSelected.length !== this.selectedClinicIds.length) {
          this.selectedClinicIds = nextSelected;
        }
        this.selectedClinicDetail = null;
      } catch (error) {
        this.pageError = error.response?.data?.detail || 'Unable to load state dashboard data.';
      } finally {
        if (!silent) {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.selector-card,
.scope-insight-card,
.chart-card,
.detail-panel {
  padding: 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.selector-card,
.scope-insight-card {
  margin-bottom: 18px;
}

.scope-insight-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.scope-insight-card__icon {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  border-radius: 14px;
  background: rgba(15, 186, 178, 0.12);
  color: #0fbab2;
  font-size: 1.1rem;
}

.selector-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ghost-chip {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--ui-border);
  background: #ffffff;
  color: var(--ui-muted);
  font-weight: 650;
  cursor: pointer;
}

.ghost-chip:hover {
  border-color: rgba(15, 186, 178, 0.4);
  color: #0b7f79;
}

.selected-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.clinic-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 186, 178, 0.28);
  background: rgba(15, 186, 178, 0.1);
  color: #0b7f79;
  font-size: 0.8rem;
  font-weight: 650;
  cursor: pointer;
}

.clinic-search {
  margin-bottom: 14px;
}

.clinic-option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  max-height: 280px;
  overflow: auto;
  padding-right: 4px;
}

.clinic-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
}

.clinic-option span {
  min-width: 0;
}

.clinic-option strong,
.clinic-option small {
  display: block;
}

.clinic-option strong {
  color: var(--ui-text);
  font-size: 0.88rem;
}

.clinic-option small {
  margin-top: 3px;
  color: var(--ui-muted);
}

.empty-selector {
  grid-column: 1 / -1;
  padding: 18px;
  text-align: center;
  color: var(--ui-muted);
  border: 1px dashed var(--ui-border);
  border-radius: 10px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.metric-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.metric-card__icon {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 12px;
  font-size: 1rem;
}

.metric-card__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.metric-card__label {
  color: var(--ui-muted);
  font-size: 0.78rem;
  font-weight: 650;
}

.metric-card__value {
  color: var(--ui-text);
  font-size: 1.55rem;
  line-height: 1;
  font-weight: 700;
}

.metric-card__hint {
  color: var(--ui-muted);
  font-size: 0.8rem;
  line-height: 1.45;
}

.metric-card--primary .metric-card__icon {
  background: rgba(15, 186, 178, 0.12);
  color: #0fbab2;
}

.metric-card--success .metric-card__icon {
  background: rgba(35, 189, 115, 0.12);
  color: #1d8c58;
}

.metric-card--warning .metric-card__icon {
  background: rgba(253, 185, 19, 0.15);
  color: #8a6500;
}

.metric-card--danger .metric-card__icon {
  background: rgba(220, 54, 46, 0.12);
  color: #dc362e;
}

.clickable-row {
  cursor: pointer;
}

.detail-panel {
  margin-top: 18px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.state-chart-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.chart-card {
  grid-column: span 6;
  padding: 16px;
}

.chart-card--wide {
  grid-column: 1 / -1;
}

.chart-card--span-8 {
  grid-column: span 8;
}

.chart-card--span-6 {
  grid-column: span 6;
}

.chart-card--span-4 {
  grid-column: span 4;
}

.chart-wrap {
  min-height: 280px;
  position: relative;
}

.chart-wrap--compact {
  min-height: 230px;
}

.empty-chart {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--ui-border);
  border-radius: var(--ui-radius);
  color: var(--ui-muted);
  text-align: center;
}

.camera-quality-table {
  margin-top: 14px;
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  overflow: hidden;
}

.camera-quality-row {
  display: grid;
  grid-template-columns: minmax(180px, 1.5fr) repeat(3, minmax(100px, 1fr));
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  color: var(--ui-text);
  font-size: 0.86rem;
}

.camera-quality-row:first-child {
  border-top: 0;
}

.camera-quality-row--header {
  background: #f8fafc;
  color: var(--ui-muted);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@media (max-width: 1199px) {
  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .clinic-option-grid,
  .metrics-grid,
  .detail-grid,
  .state-chart-grid {
    grid-template-columns: 1fr;
  }

  .selector-actions {
    width: 100%;
  }

  .chart-wrap,
  .chart-wrap--compact {
    min-height: 220px;
  }

  .chart-card,
  .chart-card--wide,
  .chart-card--span-8,
  .chart-card--span-6,
  .chart-card--span-4 {
    grid-column: 1 / -1;
  }

  .empty-chart {
    min-height: 180px;
  }

  .metric-card {
    padding: 14px;
  }

  .metric-card__value {
    font-size: 1.35rem;
  }

  .selector-card,
  .scope-insight-card,
  .chart-card,
  .detail-panel {
    padding: 14px;
  }

  .clinic-option-grid {
    max-height: 220px;
  }

  .camera-quality-table {
    overflow-x: auto;
  }

  .camera-quality-row {
    min-width: 680px;
  }
}
</style>
